import React, { useEffect, useState } from 'react';
import activitiesService, { Activity } from '../../services/crm/activitiesService';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import { formatActivityContent } from '../../utils/activityFormatter';

type EntityType = 'contact' | 'company' | 'deal';

interface NotesPanelProps {
  entityType?: EntityType;
  entityId?: string;
  parentActivityId?: string;
  title?: string;
}

const NotesPanel: React.FC<NotesPanelProps> = ({ entityType, entityId, parentActivityId, title = 'Notes' }) => {
  const { activeOrgId } = useCrmOrgStore();
  const [items, setItems] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState('');

  const canListByEntity = !!(activeOrgId && entityType && entityId);
  const canListByParent = !!(activeOrgId && parentActivityId);

  const load = async () => {
    if (!activeOrgId) return;
    setLoading(true); setError(null);
    try {
      if (canListByParent) {
        const resp = await activitiesService.list({ organization_id: activeOrgId, parent_activity_id: parentActivityId!, limit: 50, offset: 0 });
        const notes = (resp.data || []).filter(a => a.type === 'note');
        setItems(notes);
      } else if (canListByEntity) {
        const resp = await activitiesService.list({ organization_id: activeOrgId, entity_type: entityType!, entity_id: entityId!, limit: 50, offset: 0 });
        const notes = (resp.data || []).filter(a => a.type === 'note');
        setItems(notes);
      } else {
        setItems([]);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeOrgId, entityType, entityId, parentActivityId]);

  const addNote = async () => {
    if (!activeOrgId) return;
    if (!content.trim()) return;
    setLoading(true); setError(null);
    try {
      await activitiesService.create({
        organization_id: activeOrgId,
        entity_type: entityType || 'company',
        entity_id: entityId || '',
        type: 'note',
        content,
        parent_activity_id: parentActivityId || undefined
      });
      setContent('');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">{title}</h3>
      </div>
      <div className="flex items-start gap-2">
        <textarea className="border rounded p-2 w-full" rows={3} placeholder="Write a note..." value={content} onChange={e => setContent(e.target.value)} />
        <button className="bg-blue-600 text-white rounded px-3 h-10" onClick={addNote} disabled={loading || !activeOrgId}>Add</button>
      </div>
      {error && <div className="text-red-600 text-sm">{error}</div>}
      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="space-y-2">
          {items.map(n => (
            <div key={n.id} className="border rounded p-2">
              <div className="text-xs text-gray-500">{new Date(n.created_at).toLocaleString()}</div>
              <div className="text-sm whitespace-pre-wrap">{formatActivityContent(n as any)}</div>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-gray-500">No notes yet.</div>}
        </div>
      )}
    </div>
  );
};

export default NotesPanel;
